import React, { useContext } from "react";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";

// Import Contexts
import { ClinicContext, LanguageContext, ScreenContext } from "contexts";

// Import Components
import { TitleBar, PrimaryButton } from "components/index";

import { Language, ProcedureType, TScreen } from "api/type";
import { formatProcedureName } from "utils/formatProcedureName";
import { getTranslation } from "utils/translate";
import { procedureTypeSpanishMap } from "api/constants";
import { mixpanelTrack } from "utils/mixpanel";

const tummyTuckPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/tummy-tuck-top-aes.webm";
const bblPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/bbl-top-aes.webm";
const breastAugPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-aug-top-aes.webm";
const breastLiftPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-lift-top-aes.webm";
const breastReductionPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-reduction-top-aes.webm";
const breastLiftAndAugPreviewWebm =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-lift-and-aug-top-aes.webm";

const tummyTuckPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/tummy-tuck-top-aes.mp4";
const bblPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/bbl-top-aes.mp4";
const breastAugPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-aug-top-aes.mp4";
const breastLiftPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-lift-top-aes.mp4";
const breastReductionPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-reduction-top-aes.mp4";
const breastLiftAndAugPreview =
  "https://www.clinic-assets.clinic-os.com/top-aesthetics/video-preview/breast-lift-and-aug-top-aes.mp4";

const languageOptions = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

type ScreenVideoProps = {
  selectedProcedure: ProcedureType | null;
};

const ScreenVideo: React.FC<ScreenVideoProps> = ({ selectedProcedure }) => {
  // Context
  const [language, setLanguage] = useContext<Language>(LanguageContext);
  const clinic = useContext(ClinicContext);

  const setScreen = useContext(ScreenContext)[1];

  React.useEffect(() => {
    if (!clinic || !selectedProcedure || !language) return;
    if (document.referrer.includes("staging")) return;
    mixpanelTrack("Form Opened", [
      {
        propertyName: "Clinic Name",
        propertyValue: clinic.clinic_name,
      },
      {
        propertyName: "Procedure",
        propertyValue: selectedProcedure,
      },
      {
        propertyName: "Language",
        propertyValue: language,
      },
    ]);
  }, [clinic, language, selectedProcedure]);

  const getVideoSourceWebm = () => {
    if (selectedProcedure === ProcedureType.TUMMY_TUCK)
      return tummyTuckPreviewWebm;
    if (selectedProcedure === ProcedureType.BBL) return bblPreviewWebm;
    if (selectedProcedure === ProcedureType.BREAST_AUGMENTATION)
      return breastAugPreviewWebm;
    if (selectedProcedure === ProcedureType.BREAST_LIFT)
      return breastLiftPreviewWebm;
    if (selectedProcedure === ProcedureType.BREAST_REDUCTION)
      return breastReductionPreviewWebm;
    if (selectedProcedure === ProcedureType.BREAST_LIFT_AND_AUGMENTATION)
      return breastLiftAndAugPreviewWebm;
    return breastAugPreview;
  };

  const getVideoSource = () => {
    if (selectedProcedure === ProcedureType.TUMMY_TUCK) return tummyTuckPreview;
    if (selectedProcedure === ProcedureType.BBL) return bblPreview;
    if (selectedProcedure === ProcedureType.BREAST_AUGMENTATION)
      return breastAugPreview;
    if (selectedProcedure === ProcedureType.BREAST_LIFT)
      return breastLiftPreview;
    if (selectedProcedure === ProcedureType.BREAST_REDUCTION)
      return breastReductionPreview;
    if (selectedProcedure === ProcedureType.BREAST_LIFT_AND_AUGMENTATION)
      return breastLiftAndAugPreview;
    return breastAugPreview;
  };

  if (!selectedProcedure) return null;

  return (
    <div className="screen-container">
      <TitleBar
        centerTitle
        showLogo={false}
        name={
          language === "es"
            ? procedureTypeSpanishMap[selectedProcedure] ||
              "Procedimiento desconocido"
            : formatProcedureName(selectedProcedure)
        }
        onClickBackButton={null}
      />
      <Dropdown
        options={languageOptions}
        onChange={(value) => {
          if (value.value === "es") setLanguage("es");
          else setLanguage("en");
        }}
        placeholder="language: auto"
        className="dropdown"
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          background: "white",
        }}
      >
        <video
          width="100%"
          height="auto"
          controls={false}
          playsInline
          autoPlay
          loop
          muted
        >
          <source src={getVideoSource()} type="video/mp4" />
          <source src={getVideoSourceWebm()} type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </div>
      <PrimaryButton
        onClick={() => {
          setScreen(TScreen.BODY_SCAN_INSTRUCTIONS_SCREEN);
        }}
        fullWidth
        text={getTranslation("continue", language)}
      />
    </div>
  );
};

export default ScreenVideo;
